class Quiz extends HTMLElement {
  constructor() {
    super();
    this.quiz = this.querySelector("#customQuizPage");
    this.showProductsButton = this.querySelector(".show-products-button");
    this.nextQuestionButton = this.querySelector(".quiz-next-question");
    this.previousQuestionButton = this.querySelector(".quiz-previous-question");
    this.backToQuizButton = this.querySelector(".back-to-quiz-button");
    this.findStoreButton = this.querySelector(".find-store-button");
    this.questionSteps = this.querySelector(".question-steps");
    this.answers = {};
    this.allQuestions = this.querySelectorAll(".question");
    this.products = [];
    this.filteredProducts = [];
    this.currentQuestion = 0;
    this.quizFoundProductsTitle = this.getAttribute(
      "data-quiz-found-products-title"
    );
    this.yourChoiceTitle = this.getAttribute("data-your-choice-title");
    this.noResultsMessage = this.getAttribute("data-no-results-message");
    this.addToCartText = this.getAttribute("data-add-to-cart-text");
    this.soldOutText = this.getAttribute("data-sold-out-text");
    this.cartAddUrl = this.getAttribute("data-cart-add-url") || "/cart/add";
    this.quizWrapper = this.querySelector(".quiz-wrapper");
    this.quizPage = this.querySelector(".main-quiz--page");
  }

  connectedCallback() {
    this.loadProductsFromServer();

    this.nextQuestionButton.addEventListener("click", () => {
      let selectedAnswer = false;
      this.allQuestions[this.currentQuestion]
        .querySelectorAll(".question-option")
        .forEach((option) => {
          if (option.dataset.selected) {
            selectedAnswer = true;
          }
        });
      if (!selectedAnswer) {
        this.allQuestions[this.currentQuestion].classList.add("not-answered");
        setTimeout(() => {
          this.allQuestions[this.currentQuestion].classList.remove(
            "not-answered"
          );
        }, 2000);
        return;
      }
      if (this.currentQuestion === this.allQuestions.length - 1) {
        Promise.resolve(this.productsReady).then(() => {
          this.filteredProducts = this.filterProducts(this.answers);
          this.renderProducts(this.filteredProducts, this.quizPage);
        });
        return;
      }
      this.previousQuestionButton.style.removeProperty("display");
      this.previousQuestionButton.classList.add("back-btn-visible");
      this.currentQuestion++;
      this.questionSteps
        .querySelector(".active-step")
        .classList.remove("active-step");
      this.questionSteps
        .querySelector(`[data-question-step="${this.currentQuestion}"]`)
        .classList.add("active-step");
      this.allQuestions.forEach((question) => {
        question.classList.remove("active-question", "not-answered");
      });
      this.allQuestions[this.currentQuestion].classList.add("active-question");
    });

    this.previousQuestionButton.addEventListener("click", () => {
      if (this.currentQuestion <= 0) return;
      if (this.currentQuestion === 1) {
        this.previousQuestionButton.style.display = "none";
        this.previousQuestionButton.classList.remove("back-btn-visible");
      }
      this.currentQuestion--;
      this.questionSteps
        .querySelector(".active-step")
        .classList.remove("active-step");
      this.questionSteps
        .querySelector(`[data-question-step="${this.currentQuestion}"]`)
        .classList.add("active-step");
      this.allQuestions.forEach((question) => {
        question.classList.remove("active-question");
      });
      this.allQuestions[this.currentQuestion].classList.add("active-question");
    });

    this.allQuestions.forEach((question) => {
      question.querySelectorAll(".question-option").forEach((option) => {
        option.addEventListener("click", () => {
          if (!Array.isArray(this.answers[this.currentQuestion])) {
            this.answers[this.currentQuestion] = [];
          }
          const value = option.dataset.questionOption;
          if (option.dataset.selected) {
            delete option.dataset.selected;
            option.classList.remove("active-option");
            this.answers[this.currentQuestion] = this.answers[this.currentQuestion].filter(
              (v) => v !== value
            );
          } else {
            option.dataset.selected = true;
            option.classList.add("active-option");
            this.answers[this.currentQuestion].push(value);
          }
        });
      });
    });
  }

  loadProductsFromServer() {
    const productsDataElement = this.querySelector("#quiz-products-data");
    this.products = this.parseProductsData(productsDataElement);
    // Must always settle: the results screen waits on it.
    this.productsReady = this.loadRemainingProductPages(
      productsDataElement
    ).catch((error) => {
      console.error("Error loading quiz products:", error);
    });
  }

  parseProductsData(productsDataElement) {
    if (!productsDataElement) return [];
    try {
      const data = JSON.parse(productsDataElement.textContent);
      return data.products || [];
    } catch (error) {
      console.error("Error parsing products data:", error);
      return [];
    }
  }

  // A Liquid for loop is capped at 50 products, so the section paginates the
  // collection by 50 and the remaining pages are pulled in over the Section
  // Rendering API and merged into this.products.
  async loadRemainingProductPages(productsDataElement) {
    const sectionId = this.dataset.sectionId;
    if (!productsDataElement || !sectionId) return;

    const currentPage =
      parseInt(productsDataElement.dataset.currentPage, 10) || 1;
    const totalPages = parseInt(productsDataElement.dataset.totalPages, 10) || 1;
    if (totalPages <= 1) return;

    // Paginated lists can get their own query param key, so use the one Liquid reports.
    const pageParam = productsDataElement.dataset.pageParam || "page";

    const pages = [];
    for (let page = 1; page <= totalPages; page++) {
      if (page !== currentPage) pages.push(page);
    }

    const pageResults = await Promise.all(
      pages.map((page) => this.fetchProductsPage(sectionId, page, pageParam))
    );

    const seenIds = new Set(this.products.map((product) => product.id));
    pageResults.flat().forEach((product) => {
      if (seenIds.has(product.id)) return;
      seenIds.add(product.id);
      this.products.push(product);
    });
  }

  async fetchProductsPage(sectionId, page, pageParam) {
    const url = `${window.location.pathname}?section_id=${encodeURIComponent(
      sectionId
    )}&${encodeURIComponent(pageParam)}=${page}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html"
      );
      return this.parseProductsData(
        html.getElementById("quiz-products-data")
      );
    } catch (error) {
      console.error(`Error loading quiz products page ${page}:`, error);
      return [];
    }
  }

  filterProducts(filters) {
    const toArray = (v) => (Array.isArray(v) ? v : [v]);

    const filtered = this.products.filter((p) => {
      const core = p.quiz_product_core.value
        ? toArray(filters[0]).some((f) =>
            this.matchesValue(p.quiz_product_core.value, f)
          )
        : false;
      const softness = p.quiz_product_softness.value
        ? toArray(filters[1]).some((f) =>
            this.matchesValue(p.quiz_product_softness.value, f)
          )
        : false;
      const sleepPosition = p.quiz_product_sleep_position.value
        ? toArray(filters[2]).some((f) =>
            this.matchesFilter(p.quiz_product_sleep_position.value, f)
          )
        : false;
      const kilograms = p.quiz_product_kilograms.value
        ? toArray(filters[3]).some((f) =>
            this.matchesValue(p.quiz_product_kilograms.value, f)
          )
        : false;
      const sizes = p.quiz_product_sizes.value
        ? toArray(filters[4]).some((f) =>
            [].concat(p.quiz_product_sizes.value).some((size) =>
              this.matchesSize(size, f)
            )
          )
        : false;
      const price = this.matchingVariants(p, filters).length > 0;

      return core && softness && sleepPosition && kilograms && sizes && price;
    });

    return filtered;
  }

  // Quiz answers are downcased by the section and the option lists are split on
  // ", ", so stray case and whitespace must not decide a match.
  normalizeValue(value) {
    return String(value).trim().toLowerCase();
  }

  // Metafields are lists, but a plain text metafield still has to keep working,
  // so arrays match per entry and strings match as a substring.
  matchesValue(productValue, filterValue) {
    const filter = this.normalizeValue(filterValue);
    if (Array.isArray(productValue)) {
      return productValue.some((val) => this.normalizeValue(val) === filter);
    }
    return this.normalizeValue(productValue).includes(filter);
  }

  // A size can have several variants (e.g. different heights), so every variant
  // in one of the chosen sizes whose price fits the budget counts.
  matchingVariants(product, filters) {
    const toArray = (v) => (Array.isArray(v) ? v : [v]);
    return (product.quiz_product_price.value || []).filter(
      (v) =>
        toArray(filters[4]).some((f) => this.matchesSize(v.size, f)) &&
        toArray(filters[5]).some((f) => this.matchesPriceFilter(v.price, f))
    );
  }

  // Prefer a variant that is in stock; fall back to one that is not so the
  // button can still show as sold out.
  quizVariant(product) {
    const variants = this.matchingVariants(product, this.answers);
    return variants.find((v) => v.available) || variants[0];
  }

  renderAddToCart(product) {
    const variant = this.quizVariant(product);
    if (!variant || !variant.id) return "";
    return `
      <form class="quiz-add-to-cart-form" method="post" action="${this.cartAddUrl}">
        <input type="hidden" name="id" value="${variant.id}">
        <input type="hidden" name="quantity" value="1">
        <button type="submit" class="btn btn--primary quiz-add-to-cart-button"${
          variant.available ? "" : " disabled"
        }>
          ${variant.available ? this.addToCartText : this.soldOutText}
        </button>
        <p class="quiz-add-to-cart-error" role="alert"></p>
      </form>
    `;
  }

  themeSection(type) {
    const instances = window.Shopify?.theme?.sections?.instances || [];
    return instances.find((section) => section.type === type);
  }

  // With the quick cart (drawer) enabled, add over Ajax and open the drawer the
  // way the theme's product form does; otherwise the form posts normally and
  // Shopify redirects to the cart page.
  async onAddToCart(event) {
    const quickCart = this.themeSection("quick-cart");
    if (!quickCart) return;
    event.preventDefault();

    const form = event.currentTarget;
    const button = form.querySelector("button[type='submit']");
    const error = form.querySelector(".quiz-add-to-cart-error");
    button.disabled = true;
    button.classList.add("loading");
    error.textContent = "";

    try {
      const response = await fetch(`${this.cartAddUrl}.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || response.statusText);
      }

      quickCart.refreshQuickCart();
      quickCart.openQuickCart();
      this.refreshHeaderCartCount();
      this.refreshThemeCartData();
      document.dispatchEvent(
        new CustomEvent("cart:item-added", { detail: { product: data } })
      );
    } catch (err) {
      console.error("Error adding quiz product to cart:", err);
      error.textContent = err.message;
    } finally {
      button.disabled = false;
      button.classList.remove("loading");
    }
  }

  // The header only re-renders its cart icon on the theme's internal
  // "cart:updated" event, which outside scripts can't emit.
  async refreshHeaderCartCount() {
    const header = this.themeSection("header");
    if (!header) return;
    const response = await fetch(`/?section_id=${encodeURIComponent(header.id)}`);
    const html = new DOMParser().parseFromString(
      await response.text(),
      "text/html"
    );
    [
      ".header__content-desktop [data-header-cart]",
      ".header__content-mobile [data-header-cart]",
    ].forEach((selector) => {
      const updated = html.querySelector(selector);
      const current = document.querySelector(selector);
      if (updated && current) current.innerHTML = updated.innerHTML;
    });
  }

  async refreshThemeCartData() {
    const cart = await (await fetch("/cart.js")).json();
    if (window.theme) window.theme.cartData = cart;
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
  }

  // Sizes are written as "90/200 см.", "90/200 см", "90 x 200" etc. across the
  // quiz settings and variant options, so compare only the dimensions.
  matchesSize(productSize, filterSize) {
    const dimensions = (value) => (String(value).match(/\d+/g) || []).join("x");
    const filter = dimensions(filterSize);
    return filter !== "" && dimensions(productSize) === filter;
  }

  matchesFilter(productValues, filterValue) {
    const filter = this.normalizeValue(filterValue);
    return [].concat(productValues).some((val) =>
      String(val)
        .split("/")
        .some((part) => this.normalizeValue(part) === filter)
    );
  }

  matchesPriceFilter(price, filter) {
    if (!filter) return false;
    const range = filter.match(/([\d\s]+)\s*[-–]\s*([\d\s]+)/);
    if (range)
      return (
        price >= parseFloat(range[1].replace(/\s/g, "")) &&
        price <= parseFloat(range[2].replace(/\s/g, ""))
      );
    const upTo = filter.match(/до\s*([\d\s]+)/i);
    if (upTo) return price <= parseFloat(upTo[1].replace(/\s/g, ""));
    const above = filter.match(/над\s*([\d\s]+)/i);
    if (above) return price > parseFloat(above[1].replace(/\s/g, ""));
    return false;
  }

  renderProducts(products, placeToRender) {
    const oldContainer = this.querySelector(".products-container");
    if (oldContainer) oldContainer.remove();

    const answerKeys = [
      "quiz_product_core",
      "quiz_product_softness",
      "quiz_product_sleep_position",
      "quiz_product_kilograms",
      "quiz_product_sizes",
      "quiz_product_price",
    ];
    const answerIcons = ["🛏️", "✔️", "🛌", "⚖️", "🛏️", "€"];
    const approvedSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="0.359375" y="0.920898" width="23" height="23" rx="1.91862" fill="#E2EAEC"/>
    <path d="M10.798 16.5507C10.7965 16.5506 10.7948 16.5506 10.7933 16.5505C10.7324 16.5467 10.676 16.5182 10.6366 16.4717L6.61562 11.7088C6.54667 11.6271 6.54507 11.5083 6.61192 11.4247C6.67876 11.3413 6.79507 11.3167 6.89002 11.3661L10.6717 13.3365C10.7035 13.3531 10.7421 13.3473 10.7681 13.3226L16.9147 7.42118C16.9979 7.34122 17.1286 7.33765 17.216 7.41329C17.3034 7.48892 17.3189 7.61864 17.2516 7.71258L11.0163 16.4326C11.0074 16.4453 10.9971 16.4567 10.9858 16.4673L10.9589 16.492C10.9151 16.532 10.857 16.5532 10.798 16.5507Z" fill="#1F6378" stroke="#1F6378" stroke-width="0.479655"/>
    </svg>`;

    const fields = Object.entries(this.answers).map(([index, value], i) => ({
      key: answerKeys[i],
      label: Array.isArray(value) ? value.join(", ") : value,
      icon: answerIcons[i],
      value,
    }));

    const productsContainer = document.createElement("div");
    productsContainer.classList.add("quiz-products-container");
    productsContainer.innerHTML = `
      ${
        products.length
          ? `<h3 class="quiz-found-products-title">${this.quizFoundProductsTitle}</h3>`
          : ""
      }
      <div class="comparison-grid">
          <div class="your-choice-container">
            <h3 class="your-choice-text-container">${this.yourChoiceTitle}</h3>
            <div class="your-choice-row-container">
              ${fields
                .map(
                  (field, i) => `
            <div class="your-choice-row">
              <span class="quiz-answers-icon">${field.icon}</span>
              <span class="label">${field.label ? field.label : ""}</span>
            </div>
            `
                )
                .join("")}
            </div>
          </div>
        ${
          products.length
            ? ""
            : `<div class="quiz-no-results" role="status">${this.noResultsMessage}</div>`
        }
        <div class="products-container">
        ${products
          .map(
            (product) => `
            <div class="quiz-product-information">
              <div class="quiz-product-image">
                <img src="${product.featuredImage.url}" alt="${
              product.title
            }" />
              </div>
              <div class="quiz-product-info">
                <div class="product-info-item your-choice-row">
                  <a class="label quiz-product-title-link" href="${
                    product.url
                  }">${product.title}</a>
                </div>
              </div>
              ${this.renderAddToCart(product)}
            </div>
          `
          )
          .join("")}
        </div>
      </div>
      <div class="quiz-actions">
        <button class="quiz-navigation-button back-to-quiz-button">
          ${document.querySelector(".back-to-quiz-button").innerHTML}
        </button>
        <a class="quiz-navigation-button find-store-button" href="${
          this.findStoreButton.href
        }" target="_blank">
          ${this.findStoreButton.innerHTML}
        </a>
      </div>
    `;

    placeToRender.after(productsContainer);
    placeToRender.remove();

    productsContainer
      .querySelectorAll(".quiz-add-to-cart-form")
      .forEach((form) =>
        form.addEventListener("submit", (event) => this.onAddToCart(event))
      );

    productsContainer
      .querySelector(".back-to-quiz-button")
      .addEventListener("click", () => {
        productsContainer.remove();
        this.currentQuestion = this.allQuestions.length - 1;
        this.allQuestions.forEach((question, idx) => {
          question.classList.toggle(
            "active-question",
            idx === this.currentQuestion
          );
        });
        this.questionSteps
          .querySelectorAll(".question-step")
          .forEach((step, idx) => {
            step.classList.toggle("active-step", idx === this.currentQuestion);
          });
        this.quizWrapper.append(this.quizPage);
      });
  }
}

if (!customElements.get("quiz-element")) {
  customElements.define("quiz-element", Quiz);
}

class ContactForm extends HTMLElement {
  constructor() {
    super();
    this.contactForm = this.querySelector("#contact-form");
    this.quizElement = document.querySelector("quiz-element");
  }

  connectedCallback() {
    this.contactForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const email = this.contactForm.email.value;
      const answers = this.quizElement.answers;

      console.log("Email:", email);
      console.log("Quiz Answers:", answers);

      // send email logic here...
      fetch("/contact", {
        method: "POST",
        body: JSON.stringify({ email, answers }),
      });
    });
  }
}

if (!customElements.get("contact-form")) {
  customElements.define("contact-form", ContactForm);
}
